import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthMeResponse } from '@ozon-ai/apigateway/contracts';
import { api } from '../api/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  user: AuthMeResponse['user'] | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isAdmin: false,
  user: null,
  login: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState<AuthMeResponse['user'] | null>(null);

  const applyMe = useCallback((me: AuthMeResponse) => {
    setIsAuthenticated(true);
    setIsAdmin(me.isAdmin);
    setUser(me.user);
    setError(null);
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const me = await api.get<AuthMeResponse>('/auth/me');
      applyMe(me);
    } catch {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [applyMe]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const login = useCallback(
    async (username: string, password: string) => {
      setError(null);
      try {
        const result = await api.post<AuthMeResponse>('/auth/login', { username, password });
        applyMe(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Не удалось войти';
        setError(message);
        throw err;
      }
    },
    [applyMe]
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
      setError(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        isAdmin,
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
